'use client';
import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

import WordmarkBlack from 'public/img/logo/vivrelle-wordmark-black.svg';
import Search from 'public/img/icon/search-51x53.svg';
import Menu from 'public/img/icon/hamburger-menu-icon.svg';
import Heart from 'public/img/icon/like-52x49.svg';
import ShoppingBag from 'public/img/icon/shopping-bag.svg';
import UserIcon from 'public/img/icon/user-55x55.svg';
import CloseIcon from 'public/img/icon/close.svg';
import MobileMenu from '@/components/common/MobileMenu/MobileMenu';
import MegaMenu from '@/components/common/MegaMenu/MegaMenu';
import Button from '@/components/common/Button/Button';
import Text from '@/components/common/Text/Text';
import SearchBarTextElement from '@/components/common/SearchBarTextElement/SearchBarTextElement';

import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { useLoggedInUser } from '@/components/hook/user';
import { useCart } from '@/components/hook/cart';
import { useSearchContext } from '@/components/hook/search';
import { useScrollLock } from '@/components/hook/scroll';
import { useFlags } from '@/components/hook/flags';

import { FLAG_KEYS } from '@/const/flags';
import { SIGNUP_ROUTES } from '@/const/signup';
import { BuilderImageProps } from '@/types/Builder';
import { USER_STATUS } from '@/types/User';

import analytics from '@/util/analytics';

import './Header.style.scss';
import { ReactivateAnalyticsMetadata } from '@/components/hook/analytics';
import { CACHE_TIME } from '@/const/cache';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import PriveeWordmarkBlack from 'public/img/logo/reserve-logo.svg';
import VivrelleMorganStewartLogo from 'public/img/logo/vivrelle-morgan-stewart-logo.png';
import { priveeLandingPath } from '@/util/user_client';

export type HeaderCMSContent = {
  contentKey: string;

  desktopHeaderCard?: BuilderImageProps & {
    url?: string;
  };

  desktopHeaderCardSecondary?: BuilderImageProps & {
    url?: string;
  };

  mobileHeaderCard1?: BuilderImageProps & {
    url?: string;
  };

  mobileHeaderCard2?: BuilderImageProps & {
    url?: string;
  };

  /** Last section of the mega menu that contains featured links editable via CMS */
  featuredLinks?: {
    /** Text to display to the end user. Also used as the alt/aria-label when an image is rendered. */
    displayText: string;

    /** URL to send the user to */
    url: string;

    /** Optional image (e.g. brand wordmark) to render in place of displayText */
    imageUrl?: string;
  }[];
};

type HeaderProps = {
  className?: string;

  cmsContent: HeaderCMSContent;
};

export const HEADER_SEARCH_FORM_ID = 'header-search-form-element';

/** @deprecated Use HEADER_SEARCH_FORM_ID instead */
export const HEADER_SEARCH_FORM_DESKTOP_ID = HEADER_SEARCH_FORM_ID;
/** @deprecated Use HEADER_SEARCH_FORM_ID instead */
export const HEADER_SEARCH_FORM_MOBILE_ID = HEADER_SEARCH_FORM_ID;

function Header(props: HeaderProps) {
  const { className = '', cmsContent } = props;
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const [showMegaMenu, setShowMegaMenu] = useState<boolean>(false);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const { actions: searchActions, value: searchValue } = useSearchContext();
  const { value: flagValues } = useFlags();

  const showCollabLink = flagValues[FLAG_KEYS.MORGAN_STEWART_NAVBAR];
  const morganStewartCollabLink = {
    href: '/collection/morganstewart',
    logo: VivrelleMorganStewartLogo,
    logoHeight: 28,
    alt: 'Vivrelle x Morgan Stewart wordmark'
  };

  const user = useLoggedInUser();

  const { value: cart } = useCart();
  const router = useRouter();
  const { isIOSWebView } = useContextBroker();

  // State for reactivation analytics data (fetched for cancelled users)
  const [reactivateAnalyticsMetadata, setReactivateAnalyticsMetadata] =
    useState<ReactivateAnalyticsMetadata>({
      membership_tier: '',
      add_ons: false,
      insurance_coverage: ''
    });

  // Fetch previous subscription data for cancelled users (for analytics)
  useEffect(() => {
    const fetchReactivationData = async () => {
      if (user?.status !== USER_STATUS.CANCELLED) return;

      try {
        const response = await fetch('/api/user/reactivate', {
          method: 'GET',
          headers: { 'content-type': 'application/json' },
          next: { revalidate: CACHE_TIME.FIVE_MINUTES }
        });

        if (response.ok) {
          const json = await response.json();
          if (json.success && json.data) {
            const subscriptionProduct = json.data
              .subscriptionProduct as SubscriptionProduct | null;
            const insuranceProduct = json.data
              .insuranceProduct as InsuranceProduct | null;

            setReactivateAnalyticsMetadata({
              membership_tier: subscriptionProduct?.name ?? '',
              add_ons: false, // Cancelled users don't have active add-ons
              insurance_coverage: insuranceProduct?.name ?? ''
            });
          }
        }
      } catch (error) {
        console.error('Error fetching reactivation data for analytics:', error);
      }
    };

    fetchReactivationData();
  }, [user?.status]);

  useScrollLock(showMobileMenu, '.MobileMenu-links-container');

  const handleCollectionEnter = () => {
    setShowMegaMenu(true);
  };

  const handleCollectionLeave = () => {
    setShowMegaMenu(false);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    searchActions.search(value);
  };

  const handleSearchBlur = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;

    if (value) {
      searchActions.clearSearch();
    }

    setSearchOpen(false);
  };

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (window.location.pathname !== '/collection') {
      // User is not on the PLP. Push the user to the PLP page.
      router.push(`/collection?query=${searchValue}`);
    }

    toggleSearch();

    // Searching interaction happens on <ProductListApp>. See
    // SEARCH QUERY EFFECT
    // To see the actual PLP search mechanism in action.
  };

  const toggleMobileMenu = (): void => {
    setShowMobileMenu(!showMobileMenu);
  };

  const toggleSearch = (): void => {
    setSearchOpen(!searchOpen);
  };

  // Check if user has payment issues (any of the 3 conditions)
  const userHasPaymentIssues =
    user?.isPastDue === true ||
    user?.status === USER_STATUS.PAST_DUE ||
    user?.status === USER_STATUS.PAYMENT_NEEDED;

  // Check if cancelled user can self-reactivate
  const canSelfReactivate =
    user?.status === USER_STATUS.CANCELLED &&
    !user?.isLocked &&
    !user?.isFraud &&
    !userHasPaymentIssues;

  return (
    // Originally, all the links in the Header were using our NextLink component.
    // These were changed to use HTML <a> so that when a user leaves the sign up
    // page before completing the registration, we can prompt them to warn them
    // that they will lose the data the inputed.

    // At the time of writing this, NextJS doesn't have an easy way to intercept
    // route change with a prompt. We're sacrificing prefecthing routes for the above
    <header
      className={cn(
        'Header',
        'shadow-bottom',
        { loggedIn: !!user },
        {
          'show-search': searchOpen
        },
        className
      )}
      onMouseLeave={handleCollectionLeave}
    >
      <MegaMenu
        className={cn({ show: showMegaMenu }, 'shadow-top')}
        cmsContent={cmsContent}
      />
      <div
        className={cn('Header-container', {
          'shadow-bottom': searchOpen
        })}
      >
        {showMobileMenu && (
          <div
            className={cn('Header-overlay')}
            onClick={toggleMobileMenu}
          />
        )}
        <div className='Header-mobile-menu'>
          <Image
            className='Header-mobile-menu-icon'
            alt='Open Menu'
            onClick={toggleMobileMenu}
            height={22}
            width={22}
            src={Menu}
          />
          <MobileMenu
            className={cn('Header-MobileMenu', { open: showMobileMenu })}
            onCloseClick={toggleMobileMenu}
            cmsContent={cmsContent}
          />
        </div>
        <div className='Header-Link-container'>
          <div className='Header-logo'>
            <a href='/'>
              <Image
                src={WordmarkBlack}
                height={20}
                width={123}
                alt='Vivrelle wordmark in black'
                priority
                onMouseEnter={handleCollectionLeave}
              />
            </a>
          </div>
          <p
            className='Header-Link-p'
            onMouseEnter={handleCollectionEnter}
          >
            <a
              className={cn('Header-Link Link link-m', {
                'Header-Link-underline': showMegaMenu
              })}
              href='/collection'
            >
              THE COLLECTION
            </a>
          </p>
          <p
            className='Header-Link-p'
            onMouseEnter={handleCollectionLeave}
          >
            <a
              className='Link Header-Link link-m uppercase'
              href='/how-it-works'
            >
              How it works
            </a>
          </p>

          {showCollabLink && (
            <p
              className='Header-Link-p Header-Link-collab'
              onMouseEnter={handleCollectionLeave}
            >
              <a
                className='Link Header-Link Header-Link-collab-link link-m'
                href={morganStewartCollabLink.href}
              >
                <Image
                  className='Header-vivrelle-logo'
                  src={morganStewartCollabLink.logo}
                  height={morganStewartCollabLink.logoHeight}
                  alt={morganStewartCollabLink.alt}
                />
              </a>
            </p>
          )}

          <p
            className='Header-Link-p'
            onMouseEnter={handleCollectionLeave}
          >
            <a
              className='Link Header-Link Header-Link-Privee'
              href={priveeLandingPath(user)}
            >
              <Image
                className='Privee-wordmark-svg'
                src={PriveeWordmarkBlack}
                height={28}
                alt='Réservé wordmark in black'
              />
            </a>
          </p>

          <Image
            className='Header-searchIcon cursor'
            alt='Search'
            height={24}
            width={24}
            src={Search}
            onClick={toggleSearch}
          />
        </div>
        <div className='Header-buttons'>
          {!user ? (
            // LOGGED OUT STATE
            <>
              <Image
                className='link-icon link-icon-search cursor'
                alt='Search'
                height={24}
                width={24}
                src={Search}
                onClick={toggleSearch}
              />
              <a
                className='Link Header-buttons-link link-m uppercase'
                href='/login'
              >
                Login
              </a>
              <a
                className='Link Header-buttons-Button apply-button link-m uppercase'
                href={SIGNUP_ROUTES.ACCOUNT}
                onClick={() => {
                  if (window.location.pathname === '/reserve') {
                    sessionStorage.setItem(
                      'applying_from_privee_landing_page',
                      'true'
                    );
                  }
                }}
              >
                <Button
                  variant='light'
                  size='small'
                >
                  Apply
                </Button>
              </a>
            </>
          ) : (
            // LOGGED IN STATE
            <>
              <a href='/collection?filters=my-wishlist'>
                <Image
                  className='link-icon link-icon-wishlist'
                  src={Heart}
                  height={22}
                  width={22}
                  alt='View your wishlist'
                />
              </a>

              <Image
                className='link-icon link-icon-search cursor'
                alt='Search'
                height={24}
                width={24}
                src={Search}
                onClick={toggleSearch}
              />

              {cart?.items?.length > 0 && (
                <a
                  className='link-icon link-cart'
                  href='/cart'
                >
                  <Image
                    src={ShoppingBag}
                    height={25}
                    width={25}
                    alt='View your shopping bag'
                  />

                  <Text
                    className='cart-item-count'
                    variant='span-s'
                  >
                    {cart.items.length}
                  </Text>
                </a>
              )}

              <a href='/account'>
                <Image
                  className='link-icon link-icon-user'
                  src={UserIcon}
                  height={24}
                  width={24}
                  alt='Open user account menu'
                />
              </a>

              {user?.status === USER_STATUS.FROZEN && (
                <a
                  className='Button dark small Header-unfreeze-cta'
                  href={
                    isIOSWebView ? '/myaccount/unfreeze' : '/account/unfreeze'
                  }
                >
                  Unfreeze
                </a>
              )}

              {canSelfReactivate && (
                <a
                  className='Button pink-400 small Header-reactivate-cta'
                  href='/account/reactivate'
                  onClick={() => {
                    analytics.track(
                      'Reactivate Navigation CTA - Clicked',
                      reactivateAnalyticsMetadata
                    );
                  }}
                >
                  Reactivate
                </a>
              )}
            </>
          )}
        </div>
      </div>
      <form
        id={HEADER_SEARCH_FORM_ID}
        className='Header-search-form shadow-bottom'
        onSubmit={handleSearchSubmit}
      >
        <SearchBarTextElement
          className='search-textField'
          placeholder='Search Vivrelle'
          value={searchValue || ''}
          onBlur={handleSearchBlur}
          onChange={handleSearchChange}
          willFocus={searchOpen}
        />
        <Image
          className='search-cancel cursor'
          alt='Close Search'
          height={22}
          width={22}
          src={CloseIcon}
        />
      </form>
    </header>
  );
}

export default Header;
